'use client';

import { Button } from '@heroui/react';
import { Icon } from '@iconify/react';
import { useRouter } from 'next/navigation';
import type { ConversationTopBarProps } from '../props';
import { DarkTooltip } from './dark-tooltip';

export function ConversationTopBar({
    backUrl,
    title,
    onNewChat,
    onRenameTitle,
    leadingAction,
    extraActions,
    className = '',
}: ConversationTopBarProps) {
    const router = useRouter();

    return (
        <div
            className={`flex shrink-0 items-center gap-3 border-b border-default-200/60 px-4 py-2 ${className}`}
        >
            {leadingAction}
            {title && (
                <span className="group/title flex items-center gap-1 truncate">
                    <span className="truncate text-medium font-bold text-foreground">{title}</span>
                    {onRenameTitle && (
                        <DarkTooltip content="Rename" size="sm" delay={400}>
                            <Button
                                isIconOnly
                                variant="light"
                                size="sm"
                                aria-label="Rename conversation"
                                onPress={onRenameTitle}
                                className="h-6 w-6 min-w-6 text-default-400 opacity-0 transition-opacity group-hover/title:opacity-100 data-[hover=true]:text-default-600"
                            >
                                <Icon icon="lucide:pencil" width={13} />
                            </Button>
                        </DarkTooltip>
                    )}
                </span>
            )}
            <div className="ml-auto flex items-center gap-2">
                {extraActions}
                <DarkTooltip content="Chat history" size="sm">
                    <Button
                        isIconOnly
                        variant="flat"
                        size="sm"
                        aria-label="Chat history"
                        onPress={() => router.push(backUrl)}
                        className="h-9 w-9 rounded-full text-default-600"
                    >
                        <Icon icon="lucide:list-clock" width={16} height={16} />
                    </Button>
                </DarkTooltip>
                {onNewChat && (
                    <DarkTooltip content="New chat" size="sm">
                        <Button
                            isIconOnly
                            size="sm"
                            variant="solid"
                            color="primary"
                            aria-label="New chat"
                            onPress={onNewChat}
                            className="h-9 w-9 rounded-full"
                        >
                            <Icon icon="lucide:plus" width={16} height={16} />
                        </Button>
                    </DarkTooltip>
                )}
            </div>
        </div>
    );
}
